/**
 * projectProgress.ts — Single source of truth for project progress logic.
 *
 * The product has exactly 4 steps. Progress is derived from `project.stage`
 * (the DB field). Nothing is marked complete unless it actually is.
 *
 * Step definitions:
 *   1. Upload Documents  — at least one document uploaded (stage past 'upload')
 *   2. AI Intake         — Erica intake completed (stage past 'intake'/'deep-probe')
 *   3. Confirm Outputs   — outputs reviewed and confirmed (stage = 'outputs' or 'dashboard')
 *   4. Push Live         — search engine activated (stage = 'dashboard')
 *
 * Stage → completion mapping:
 *   upload          → none complete (0%)
 *   context-review  → step 1 complete (25%) — docs uploaded, context reviewed
 *   intake          → step 1 complete (25%) — in intake, docs exist
 *   intake in progress — legacy stage
 *   confirmation    → step 1+2 complete (50%) — intake done, confirming outputs
 *   outputs         → step 1+2+3 complete (75%) — outputs confirmed
 *   dashboard       → all 4 complete (100%) — live
 */

import type { ProjectStage } from './contracts'

export interface ProgressStep {
  label: string
  description: string
  complete: boolean
  active: boolean        // currently in progress
}

export interface ProjectProgress {
  steps: ProgressStep[]
  percent: number        // 0 | 25 | 50 | 75 | 100
  statusLabel: string    // human-readable status pill text
  statusVariant: 'draft' | 'in-progress' | 'ready' | 'live'
}

// Map from DB stage → which of the 4 steps are done
const STAGE_COMPLETION: Record<ProjectStage, [boolean, boolean, boolean, boolean]> = {
  //                         docs   intake  outputs  live
  'upload':         [false, false,  false,  false],
  'context-review': [true,  false,  false,  false],  // legacy — treated as upload done
  'intake':         [true,  false,  false,  false],
  'deep-probe':     [true,  false,  false,  false],  // legacy — merged into intake
  'confirmation':   [true,  true,   false,  false],
  'outputs':        [true,  true,   true,   false],
  'dashboard':      [true,  true,   true,   true],
}

const STEP_DEFINITIONS = [
  {
    label: 'Upload Documents',
    description: 'JD or brief uploaded',
  },
  {
    label: 'AI Intake',
    description: 'Erica intake complete',
  },
  {
    label: 'Confirm Outputs',
    description: 'Outputs reviewed & confirmed',
  },
  {
    label: 'Push Live',
    description: 'Search engine activated',
  },
]

export function getProjectProgress(stage: ProjectStage): ProjectProgress {
  const completion = STAGE_COMPLETION[stage] ?? [false, false, false, false]
  const doneCount = completion.filter(Boolean).length
  const percent = doneCount * 25 as 0 | 25 | 50 | 75 | 100

  // The "active" step is the first incomplete one
  const activeIndex = completion.findIndex((done) => !done)

  const steps: ProgressStep[] = STEP_DEFINITIONS.map((def, i) => ({
    label: def.label,
    description: def.description,
    complete: completion[i],
    active: i === activeIndex,
  }))

  let statusLabel: string
  let statusVariant: ProjectProgress['statusVariant']

  if (stage === 'dashboard') {
    statusLabel = 'Live'
    statusVariant = 'live'
  } else if (stage === 'outputs') {
    statusLabel = 'Ready'
    statusVariant = 'ready'
  } else if (stage === 'upload') {
    statusLabel = 'Draft'
    statusVariant = 'draft'
  } else {
    statusLabel = 'In progress'
    statusVariant = 'in-progress'
  }

  return { steps, percent, statusLabel, statusVariant }
}

/** Convenience: is this project fully live (all 4 steps done)? */
export const isProjectLive = (stage: ProjectStage): boolean => stage === 'dashboard'

/** Convenience: has any real work been done (past initial upload stage)? */
export const isProjectStarted = (stage: ProjectStage): boolean => stage !== 'upload'
